import {
  Button,
  ButtonGroup,
  Divider,
  GridItem,
  Spacer,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import { sum } from 'lodash';
import { useTranslation } from 'react-i18next';
import { ViewerMode } from 'src/components/common/viewer-mode';
import MeasureToolButton from 'src/components/viewers/SheetsViewerV2/components/MeasureToolButton';
import { Pagination } from 'src/components/viewers/SheetsViewerV2/components/Pagination';
import { PDFOpacitySlider } from 'src/components/viewers/SheetsViewerV2/components/PDFOpacitySlider';
import { SheetCalibration } from 'src/components/viewers/SheetsViewerV2/components/SheetCalibration';
import SheetShapeLabelsButton from 'src/components/viewers/SheetsViewerV2/components/SheetShapeLabelsButton';
import SnappingButton from 'src/components/viewers/SheetsViewerV2/components/SnappingButton';
import WindLoadZonesToggleButton from 'src/components/viewers/SheetsViewerV2/components/WindLoadZonesToggleButton';
import { useSheetViewerV2 } from 'src/components/viewers/SheetsViewerV2/hooks/useSheetViewerV2';
import { useUserTenant } from 'src/services/auth-info';

export function SheetsViewerV2Interactions({
  readOnly,
}: {
  readOnly?: boolean;
}) {
  const {
    viewerMode,
    pagination,
    activeSheet,
    visibilityManager: {
      setIsolatedElements,
      setHiddenElements,
      hiddenElements,
      isolatedElements,
    },
  } = useSheetViewerV2();
  const { t } = useTranslation('project');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const numHiddenElements = sum(
    Object.values(hiddenElements).map((v) => v.length)
  );
  const numIsolatedElements = sum(
    Object.values(isolatedElements).map((v) => v.length)
  );

  const activeIconColor = useColorModeValue('teal.500', 'teal.200');

  const { isProtan } = useUserTenant();

  if (viewerMode !== ViewerMode.SheetsV2 || activeSheet?.isLoading) {
    return null;
  }

  return (
    <GridItem
      area="viewer-interactions"
      display="flex"
      pointerEvents="none"
      paddingTop={2}
      zIndex={999}
      sx={{
        '> *:not(:empty)': {
          pointerEvents: 'auto',
        },
        '@media (max-width: 1200px)': {
          paddingRight: 2,
        },
      }}
    >
      <Pagination {...pagination} />
      <ButtonGroup mr={2}>
        {!readOnly && <SheetCalibration />}
        <Divider borderColor={borderColor} orientation="vertical" />
        <PDFOpacitySlider />
        <SheetShapeLabelsButton />
        {isProtan && <WindLoadZonesToggleButton />}
        <Divider borderColor={borderColor} orientation="vertical" />
        <SnappingButton />
        <Divider borderColor={borderColor} orientation="vertical" />
        <MeasureToolButton />
      </ButtonGroup>
      <Spacer />
      <ButtonGroup>
        {numIsolatedElements > 0 ? (
          <Tooltip label={t('viewer-interactions.clear-isolated')}>
            <Button
              colorScheme="teal"
              variant="toggle"
              isActive
              size="xs"
              color={activeIconColor}
              onClick={() => setIsolatedElements({})}
            >
              {t('viewer-interactions.isolated-elements', {
                numIsolatedElements,
              })}
            </Button>
          </Tooltip>
        ) : null}
        {numHiddenElements > 0 ? (
          <Tooltip label="Clear hidden elements">
            <Button
              colorScheme="teal"
              variant="toggle"
              isActive
              size="xs"
              color={activeIconColor}
              onClick={() => setHiddenElements({})}
            >
              {t('viewer-interactions.hidden-elements', { numHiddenElements })}
            </Button>
          </Tooltip>
        ) : null}
      </ButtonGroup>
    </GridItem>
  );
}
